// src/pages/bussiness/profile.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Calendar,
  LifeBuoy,
  Landmark,
  CheckCircle2,
  Edit3,
  CalendarDays,
  X,
} from "lucide-react";
import EditProfilePopup from "../../components/EditProfilePopup";

import BankAccountModal from "../../components/BankAccountModal";
import SessionStatsModal from "../../components/SessionStatsModal";
import { API_ENDPOINTS } from "../../lib/config";
import { fetchWithAuth } from "../../lib/authService";

export default function BusinessProfile() {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({});
  const [performerEvents, setPerformerEvents] = useState([]);
  const [activeTab, setActiveTab] = useState("events");
  const [showEditProfile, setShowEditProfile] = useState(false);

  const [showBankModal, setShowBankModal] = useState(false);
  const [selectedVenueForStats, setSelectedVenueForStats] = useState(null);
  const [profileReloadKey, setProfileReloadKey] = useState(0);

  // Beneficiary / Bank state
  const [beneficiary, setBeneficiary] = useState(null);
  const [hasBankDetails, setHasBankDetails] = useState(true); // default true until checked
  const [dismissBankAlert, setDismissBankAlert] = useState(false);

  // Support form state
  const [supportReason, setSupportReason] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [supportResponse, setSupportResponse] = useState("");

  // Check beneficiary status
  const checkBeneficiary = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.BENEFICIARY, { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        if (data && (data.bankAccount || data.accountNumber)) {
          setBeneficiary(data);
          setHasBankDetails(true);
          return true;
        }
      }
      setBeneficiary(null);
      setHasBankDetails(false);
      return false;
    } catch (err) {
      console.error("Error fetching beneficiary:", err);
      setBeneficiary(null);
      setHasBankDetails(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("tringAuthToken");
        if (!token) {
          navigate("/");
          return;
        }

        // 1️⃣ Partner Profile
        const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER, { method: "GET" });
        if (!profRes.ok) throw new Error("Unauthorized");
        const partner = await profRes.json();
        setProfileData(partner);

        // 2️⃣ Performer Business Events
        const perfEvRes = await fetchWithAuth(API_ENDPOINTS.EVENT_PERFORMER_BUSINESS, { method: "GET" });
        if (perfEvRes.ok) {
          const perfEvents = await perfEvRes.json();
          const eventsList = Array.isArray(perfEvents) ? perfEvents : [];

          // Only fetch venue details if missing from the event object
          const missingVenueIds = [
            ...new Set(
              eventsList
                .filter((ev) => ev.venueId && (!ev.venue || (!ev.venue.address && !ev.venue.display_name)))
                .map((ev) => ev.venueId)
            ),
          ];

          const venueMap = {};
          if (missingVenueIds.length > 0) {
            await Promise.all(
              missingVenueIds.map(async (venueId) => {
                try {
                  const venueRes = await fetchWithAuth(API_ENDPOINTS.VENUE_DETAILS(venueId), { method: "GET" });
                  if (venueRes.ok) {
                    const data = await venueRes.json();
                    if (data && typeof data === "object" && Object.keys(data).length > 0) {
                      venueMap[venueId] = data;
                    }
                  }
                } catch {
                  // Fallback gracefully
                }
              })
            );
          }

          const eventsWithVenue = eventsList.map((ev) => {
            const fetchedVenue = venueMap[ev.venueId];
            const venue = (fetchedVenue && Object.keys(fetchedVenue).length > 0)
              ? { ...(ev.venue || {}), ...fetchedVenue }
              : (ev.venue || {});
            const address = (venue.address && Object.keys(venue.address).length > 0)
              ? venue.address
              : (ev.address || {});

            return {
              ...ev,
              venue,
              address,
              photoUrl: ev.photoUrl || venue.photoUrl || ev.venuePhotoUrl,
            };
          });

          eventsWithVenue.sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
          setPerformerEvents(eventsWithVenue);
        }



        // 4️⃣ Check Beneficiary / Bank Details
        const hasBank = await checkBeneficiary();
        if (!hasBank && !sessionStorage.getItem("bank_prompted_business")) {
          sessionStorage.setItem("bank_prompted_business", "true");
          // Proactively prompt partner if bank details are missing
          setShowBankModal(true);
        }
      } catch (err) {
        console.error("Business profile loading error:", err);
      }
    };

    loadData();
  }, [showEditProfile, profileReloadKey, checkBeneficiary, navigate]);

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getEventAddress = (ev) => {
    const addr = (ev?.address && Object.keys(ev.address).length > 0)
      ? ev.address
      : ev?.venue?.address;

    if (addr) {
      if (typeof addr === "string" && addr.trim()) return addr.trim();
      if (addr.formatted_address && typeof addr.formatted_address === "string" && addr.formatted_address.trim()) {
        return addr.formatted_address.trim();
      }
      const parts = [
        addr.line1,
        addr.line2,
        addr.city,
        addr.state,
        addr.postal_code,
        addr.country,
      ].filter((p) => typeof p === "string" && p.trim().length > 0);

      if (parts.length > 0) {
        return parts.join(", ");
      }
    }

    if (ev?.venue?.display_name && typeof ev.venue.display_name === "string" && ev.venue.display_name.trim()) {
      return ev.venue.display_name.trim();
    }
    if (ev?.venue?.name && typeof ev.venue.name === "string" && ev.venue.name.trim()) {
      return ev.venue.name.trim();
    }

    return "Venue Address Not Specified";
  };

  const maskAccount = (acc) => {
    if (!acc) return "•••• ••••";
    const str = String(acc);
    if (str.length <= 4) return `•••• ${str}`;
    return `•••• ${str.slice(-4)}`;
  };

  // Support form submit handler
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSupportResponse("");
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.USERS_SUPPORT, {
        method: "POST",
        body: JSON.stringify({
          reason: supportReason,
          message: supportMessage,
        }),
      });
      const text = await res.text();
      setSupportResponse(text || "Thank you! Our support team will respond shortly.");
      if (res.ok) {
        setSupportReason("");
        setSupportMessage("");
      }
    } catch (err) {
      setSupportResponse("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-full mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-7 space-y-6">
      {/* Bank Account Missing Action Prompt Banner */}
      {!hasBankDetails && !dismissBankAlert && (
        <div
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border border-amber-300/90 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm"
        >
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/30">
              <Landmark className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-amber-950 text-sm sm:text-base leading-tight">
                  Add Your Bank Account For Direct Payouts
                </h4>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-200 text-amber-900 uppercase">
                  Action Required
                </span>
              </div>
              <p className="text-xs text-amber-800/90 mt-1">
                Link your settlement bank account to receive automatic event payouts and fan gift tips directly into your account.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowBankModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-amber-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Add Bank Details
            </button>
            <button
              onClick={() => setDismissBankAlert(true)}
              className="p-2 text-amber-700 hover:text-amber-950 rounded-lg hover:bg-amber-100/50 transition-colors"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Hero Profile Card */}
      <div
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2D0A4A] via-[#46146B] to-[#72288D] text-white p-6 sm:p-8 shadow-xl shadow-purple-950/15 [transform:translateZ(0)] isolate"
      >
        {/* Background glow effects - GPU accelerated radial gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(192,132,252,0.18),transparent_50%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(129,140,248,0.15),transparent_50%)] pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          {/* Avatar and Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-5 text-center sm:text-left">
            <div className="relative group flex-shrink-0">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white/20 shadow-2xl overflow-hidden bg-purple-900">
                <img
                  src={profileData?.avatar || "/images/default-avatar.png"}
                  alt={profileData?.display_name || "DJ"}
                  className="object-cover h-full w-full"
                />
              </div>
              <span
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 ring-2 ring-[#2D0A4A] flex items-center justify-center shadow-sm"
                title="Active Business Performer"
              >
                <CheckCircle2 className="w-4 h-4 text-white" />
              </span>
            </div>

            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  {profileData?.display_name || "Business Performer"}
                </h1>
              </div>

              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-xs sm:text-sm text-purple-200">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <Phone className="w-3.5 h-3.5 text-purple-300" />
                  <span>
                    {profileData?.phone_number
                      ? profileData.phone_number.startsWith("+91")
                        ? profileData.phone_number
                        : `+91${profileData.phone_number}`
                      : "No phone linked"}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Action Toolbar */}
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-2.5 pt-3 md:pt-0">
            <button
              onClick={() => setShowEditProfile(true)}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white text-[#2D0A4A] text-xs sm:text-sm font-bold shadow-md hover:bg-purple-50 transition-colors active:scale-95"
              type="button"
            >
              <Edit3 className="w-4 h-4 text-[#72288D]" />
              <span>Edit Profile</span>
            </button>



            <button
              onClick={() => setShowBankModal(true)}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/25 text-xs sm:text-sm font-medium transition-colors active:scale-95"
              type="button"
              title={hasBankDetails && (beneficiary?.bankAccount || beneficiary?.accountNumber) ? `Linked: ${maskAccount(beneficiary.bankAccount || beneficiary.accountNumber)}` : "Add Bank Details"}
            >
              <Landmark className="w-4 h-4 text-purple-200" />
              <span>
                {hasBankDetails && (beneficiary?.bankAccount || beneficiary?.accountNumber)
                  ? `Bank (${maskAccount(beneficiary.bankAccount || beneficiary.accountNumber)})`
                  : "Bank Details"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Modern Floating Segmented Tab Bar (2 Clean Tabs: Past Events & Help & Support) */}
      <div className="grid grid-cols-2 gap-1.5 p-1.5 bg-white rounded-2xl border border-purple-100 shadow-xs">
        {[
          { id: "events", label: "Past Events", count: performerEvents.length, Icon: Calendar },
          { id: "support", label: "Help & Support", Icon: LifeBuoy },
        ].map(({ id, label, count, Icon }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all text-center ${
                isActive
                  ? "bg-gradient-to-r from-[#72288D] to-[#591E6E] text-white shadow-md shadow-purple-900/20"
                  : "text-gray-600 hover:text-[#72288D] hover:bg-purple-50/70"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${isActive ? "text-white" : "text-gray-400"}`} />
              <span className="truncate">{label}</span>
              {count !== null && count !== undefined && (
                <span
                  className={`text-[10px] sm:text-[11px] px-1.5 sm:px-2 py-0.2 sm:py-0.5 rounded-full font-bold transition-colors flex-shrink-0 ${
                    isActive ? "bg-white/20 text-white" : "bg-purple-100 text-[#72288D]"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content Container */}
      <div className="w-full min-h-[400px] flex flex-col">
        {/* EVENT OVERVIEW */}
        {activeTab === "events" && (
          <div className="space-y-6 pb-12">
            {performerEvents.length === 0 ? (
              <div className="bg-white rounded-3xl border border-purple-100 p-10 sm:p-14 text-center shadow-xs flex flex-col items-center justify-center max-w-lg mx-auto space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center text-[#72288D] shadow-inner">
                  <CalendarDays className="w-8 h-8" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-lg font-bold text-gray-900">No Events Recorded</h3>
                  <p className="text-xs sm:text-sm text-gray-500 max-w-sm">
                    When you schedule or conclude sessions at venues, their summaries, song acceptance metrics, and audience crowd stats will appear here.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/businessCalendar")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#72288D] text-white text-xs sm:text-sm font-semibold hover:bg-[#591E6E] shadow-sm transition-all"
                >
                  <Calendar className="w-4 h-4" />
                  <span>View Business Calendar</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {performerEvents.map((ev, i) => (
                  <div
                    key={ev.id || i}
                    className="bg-white rounded-2xl border border-purple-100 shadow-xs hover:shadow-md hover:border-purple-200 transition-[box-shadow,border-color] duration-150 overflow-hidden flex flex-col [transform:translateZ(0)]"
                  >
                    <div className="relative h-44 w-full bg-purple-100 overflow-hidden [transform:translateZ(0)]">
                      <img
                        src={ev.photoUrl || ev.venuePhotoUrl || "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=600&auto=format&fit=crop&q=80"}
                        alt={ev.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/75 text-white text-xs font-semibold">
                        {formatDate(ev.startAt)}
                      </div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1.5">
                          {ev.name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 mb-2.5 line-clamp-2" title={getEventAddress(ev)}>
                          {getEventAddress(ev)}
                        </p>

                      </div>

                      <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-purple-50">
                        <button
                          onClick={() => navigate(`/venue/${ev.id}/overview`)}
                          className="py-2.5 bg-[#72288D] text-white rounded-xl shadow-xs hover:bg-[#591E6E] transition text-xs font-bold"
                        >
                          Summary
                        </button>
                        <button
                          onClick={() => setSelectedVenueForStats(ev.venueId || ev.id)}
                          className="py-2.5 bg-purple-50 text-[#72288D] border border-purple-200 rounded-xl hover:bg-purple-100 transition text-xs font-bold"
                        >
                          Session Stats
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* SUPPORT */}
        {activeTab === "support" && (
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-purple-100 shadow-xs max-w-2xl mx-auto space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Partner Support Desk
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Encountered an issue with venue payouts, song queue management, or app setup? Send our support team a direct ticket.
              </p>
            </div>

            <form onSubmit={handleSupportSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Subject / Category
                </label>
                <select
                  value={supportReason}
                  onChange={(e) => setSupportReason(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-purple-200/80 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#72288D]/20 focus:border-[#72288D]"
                >
                  <option value="">Select a reason</option>
                  <option>Question about the app</option>
                  <option>Payment / Payout Issue</option>
                  <option>Event or Venue Discrepancy</option>
                  <option>Bank Account Setup</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  How can we help you?
                </label>
                <textarea
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-purple-200/80 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#72288D]/20 focus:border-[#72288D]"
                  placeholder="Describe your issue or feedback in detail..."
                />
                <p className="text-[11px] text-gray-400 mt-1 text-right">
                  {supportMessage.length}/500 characters
                </p>
              </div>

              <div className="flex justify-end items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setSupportReason("");
                    setSupportMessage("");
                    setSupportResponse("");
                  }}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 transition"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-[#72288D] text-white rounded-xl text-xs font-bold hover:bg-[#591E6E] disabled:opacity-50 transition shadow-xs"
                >
                  {submitting ? "Submitting…" : "Submit Ticket"}
                </button>
              </div>

              {supportResponse && (
                <p className="mt-4 p-3 rounded-xl bg-purple-50 text-xs text-center font-medium text-[#72288D] border border-purple-100">
                  {supportResponse}
                </p>
              )}
            </form>
          </div>
        )}
      </div>





      {/* Bank Account Modal with auto refresh on close */}
      <BankAccountModal
        isOpen={showBankModal}
        onClose={() => {
          setShowBankModal(false);
          checkBeneficiary();
        }}
      />

      {/* Edit Profile Popup */}
      {showEditProfile && (
        <EditProfilePopup
          onClose={() => setShowEditProfile(false)}
          profile={profileData}
          onSuccess={() => {
            setShowEditProfile(false);
            setProfileReloadKey((k) => k + 1);
          }}
        />
      )}


      {/* Session Statistics Modal */}
      <SessionStatsModal
        open={Boolean(selectedVenueForStats)}
        onClose={() => setSelectedVenueForStats(null)}
        venueId={selectedVenueForStats}
      />
    </div>
  );
}
